import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';
import { formatDate } from '../../i18n/utils';
import FlextimeCard from '../../components/FlextimeCard';

const ABSENCE_BADGE = {
  KRANK_MIT_ATTEST: 'bg-danger',
  KRANK_OHNE_ATTEST: 'bg-warning text-dark',
  FZT: 'badge-fzt',
};
const VACATION_BADGE = { PENDING: 'bg-warning text-dark', APPROVED: 'bg-success', REJECTED: 'bg-secondary' };

export default function AdminUserDetail() {
  const { id } = useParams();
  const userId = parseInt(id, 10);
  const { hasPermission } = useAuth();
  const { t, lang, locale } = useI18n();
  const canManageAbsences = hasPermission('manage_absences');
  const showDate = (v) => (lang === 'de' ? v : formatDate(v, lang));
  const showTime = (v) => (v ? new Date(v).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }) : '–');
  const hours = (min) => (min / 60).toFixed(2);

  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [person, setPerson] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [times, setTimes] = useState(null);
  const [vacations, setVacations] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);

  const emptyForm = { start_date: '', end_date: '', absence_type: 'KRANK_OHNE_ATTEST', notes: '' };
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const calls = [
        api.get(`/users/${userId}`),
        api.get(`/time/overview?user_id=${userId}&date_from=${year}-01-01&date_to=${year}-12-31`),
        api.get('/vacation/requests?all_users=true'),
      ];
      if (canManageAbsences) calls.push(api.get(`/absences/?user_id=${userId}&year=${year}`));
      const res = await Promise.all(calls);
      setPerson(res[0].data);
      setTimes(res[1].data);
      setVacations(res[2].data.filter((v) => v.user_id === userId && v.start_date.startsWith(String(year))));
      setAbsences(canManageAbsences ? res[3].data : []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [userId, year, canManageAbsences]);

  useEffect(() => { load(); }, [load]);

  const [compTimeEnabled, setCompTimeEnabled] = useState(false);
  useEffect(() => {
    api.get('/company-settings/').then((r) => setCompTimeEnabled(r.data.comp_time_enabled)).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setError('');
    setShowForm(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({ start_date: a.start_date, end_date: a.end_date, absence_type: a.absence_type, notes: a.notes || '' });
    setError('');
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      if (editing) {
        await api.put(`/absences/${editing.id}`, form);
      } else {
        await api.post('/absences/', { ...form, user_id: userId });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('absence.error.save'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (aid) => {
    if (!window.confirm(t('absence.confirm.delete'))) return;
    await api.delete(`/absences/${aid}`);
    await load();
  };

  if (loading && !person) return <div className="text-center"><div className="spinner-border" /></div>;
  if (loadError || !person) {
    return (
      <div>
        <div className="alert alert-danger">{t('admin.userDetail.notFound')}</div>
        <Link to="/admin/users" className="btn btn-outline-secondary btn-sm">{t('admin.userDetail.back')}</Link>
      </div>
    );
  }

  const entries = (times?.days || []).flatMap((d) => d.entries);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h4 className="mb-0">
          <i className="bi bi-person-lines-fill me-2" />{person.first_name} {person.last_name}
          <small className="text-muted ms-2"><code>{person.personnel_number}</code></small>
        </h4>
        <div className="d-flex gap-2 align-items-center">
          <select className="form-select form-select-sm" style={{ width: 100 }} value={year}
            title={t('admin.userDetail.year')} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <Link to="/admin/users" className="btn btn-outline-secondary btn-sm">
            <i className="bi bi-arrow-left me-1" />{t('admin.userDetail.back')}
          </Link>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h6 className="card-title">{t('admin.userDetail.master')}</h6>
          <dl className="row mb-0">
            <dt className="col-sm-3">{t('admin.users.personnelNumber')}</dt>
            <dd className="col-sm-9">{person.personnel_number}</dd>
            <dt className="col-sm-3">{t('admin.users.email')}</dt>
            <dd className="col-sm-9">{person.email || '–'}</dd>
            <dt className="col-sm-3">{t('admin.users.role')}</dt>
            <dd className="col-sm-9">{person.role?.name || '–'}</dd>
            <dt className="col-sm-3">{t('admin.users.col.status')}</dt>
            <dd className="col-sm-9">
              <span className={`badge ${person.is_active ? 'bg-success' : 'bg-danger'}`}>
                {person.is_active ? t('admin.users.active') : t('admin.users.inactive')}
              </span>
            </dd>
            <dt className="col-sm-3">{t('admin.users.weeklyHours')}</dt>
            <dd className="col-sm-9">{person.weekly_hours ?? '–'}</dd>
            <dt className="col-sm-3">{t('admin.users.vacationDays')}</dt>
            <dd className="col-sm-9">{person.vacation_days_per_year ?? '–'}</dd>
            <dt className="col-sm-3">{t('admin.users.entryDate')}</dt>
            <dd className="col-sm-9">{person.entry_date ? showDate(person.entry_date) : '–'}</dd>
          </dl>
        </div>
      </div>

      <FlextimeCard userId={userId} />

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between">
          <strong><i className="bi bi-clock me-2" />{t('admin.userDetail.times')}</strong>
          <span className="text-muted">{t('admin.userDetail.timesTotal', { hours: hours(times?.total_worked_minutes || 0) })}</span>
        </div>
        {entries.length === 0 ? (
          <div className="card-body text-muted">{t('admin.userDetail.noTimes')}</div>
        ) : (
          <div className="table-responsive" style={{ maxHeight: 360, overflowY: 'auto' }}>
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>{t('admin.userDetail.col.date')}</th>
                  <th>{t('admin.userDetail.col.checkIn')}</th>
                  <th>{t('admin.userDetail.col.checkOut')}</th>
                  <th>{t('admin.userDetail.col.break')}</th>
                  <th>{t('admin.userDetail.col.worked')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td>{showDate(e.date)}</td>
                    <td>{showTime(e.check_in)}</td>
                    <td>{showTime(e.check_out)}</td>
                    <td>{e.break_minutes}</td>
                    <td>{e.worked_minutes != null ? `${hours(e.worked_minutes)} h` : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card mb-4">
        <div className="card-header"><strong><i className="bi bi-airplane me-2" />{t('admin.userDetail.vacation')}</strong></div>
        {vacations.length === 0 ? (
          <div className="card-body text-muted">{t('admin.userDetail.noVacation')}</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0">
              <thead>
                <tr>
                  <th>{t('absence.col.from')}</th>
                  <th>{t('absence.col.to')}</th>
                  <th>{t('absence.col.workdays')}</th>
                  <th>{t('admin.users.col.status')}</th>
                </tr>
              </thead>
              <tbody>
                {vacations.map((v) => (
                  <tr key={v.id}>
                    <td>{showDate(v.start_date)}</td>
                    <td>{showDate(v.end_date)}</td>
                    <td>{v.days_count}</td>
                    <td><span className={`badge ${VACATION_BADGE[v.status]}`}>{t(`admin.userDetail.vacationStatus.${v.status}`)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong><i className="bi bi-bandaid me-2" />{t('admin.userDetail.absences')}</strong>
          {canManageAbsences && (
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              <i className="bi bi-plus-circle me-2" />{t('admin.userDetail.absencesAdd')}
            </button>
          )}
        </div>
        {!canManageAbsences ? (
          <div className="card-body text-muted">{t('admin.userDetail.noAbsencePermission')}</div>
        ) : (
          <>
            {showForm && (
              <div className="card-body border-bottom">
                <h6>{editing ? t('admin.userDetail.absencesEdit') : t('absence.form.title')}</h6>
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('absence.form.type')}</label>
                      <select className="form-select" value={form.absence_type}
                        onChange={(e) => setForm({ ...form, absence_type: e.target.value })}>
                        <option value="KRANK_OHNE_ATTEST">{t('absence.type.KRANK_OHNE_ATTEST')}</option>
                        <option value="KRANK_MIT_ATTEST">{t('absence.type.KRANK_MIT_ATTEST')}</option>
                        {(compTimeEnabled || form.absence_type === 'FZT') && (
                          <option value="FZT">{t('absence.type.FZT')}</option>
                        )}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('absence.form.from')}</label>
                      <input type="date" className="form-control" required value={form.start_date}
                        onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">{t('absence.form.to')}</label>
                      <input type="date" className="form-control" required value={form.end_date}
                        onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">{t('absence.form.notes')}</label>
                      <input type="text" className="form-control" value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="mt-3 d-flex gap-2">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? t('absence.form.saving') : t('absence.form.save')}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>
                      {t('absence.form.cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}
            {absences.length === 0 ? (
              <div className="card-body text-muted">{t('admin.userDetail.noAbsences')}</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0">
                  <thead>
                    <tr>
                      <th>{t('absence.col.type')}</th>
                      <th>{t('absence.col.from')}</th>
                      <th>{t('absence.col.to')}</th>
                      <th>{t('absence.col.workdays')}</th>
                      <th>{t('absence.col.notes')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {absences.map((a) => (
                      <tr key={a.id}>
                        <td><span className={`badge ${ABSENCE_BADGE[a.absence_type]}`}>{t(`absence.type.${a.absence_type}`)}</span></td>
                        <td>{showDate(a.start_date)}</td>
                        <td>{showDate(a.end_date)}</td>
                        <td>{a.days_count}</td>
                        <td>{a.notes || '–'}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={() => openEdit(a)} title={t('admin.common.edit')}>
                              <i className="bi bi-pencil" />
                            </button>
                            <button className="btn btn-outline-danger" onClick={() => handleDelete(a.id)} title={t('absence.delete')}>
                              <i className="bi bi-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

